<script setup>
// @-mention picker for WhatsApp GROUP conversations (reply mode). Lists the
// group's members (roster proxied from Evolution via the group_participants
// endpoint) and inserts a plain @<digits> token — WhatsApp binds the token to
// the mention metadata sent alongside (wa_mentions), rendering it as @Name.
import Avatar from 'next/avatar/Avatar.vue';
import { ref, computed, nextTick, watch } from 'vue';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';

const props = defineProps({
  searchKey: {
    type: String,
    default: '',
  },
  participants: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['selectParticipant']);

const listRef = ref(null);
const selectedIndex = ref(0);

const items = computed(() => {
  const search = props.searchKey?.trim().toLowerCase() || '';
  return props.participants
    .filter(participant => {
      if (!search) return true;
      const name = (participant.name || '').toLowerCase();
      return (
        name.includes(search) ||
        participant.digits.includes(search) ||
        (participant.phone || '').includes(search)
      );
    })
    .map(participant => ({
      ...participant,
      // Subtitle: the human-readable phone; the inserted token still uses the
      // participant's own jid digits (@lid in LID groups) — the form WhatsApp
      // binds mentions to.
      handle: participant.phone
        ? `+${participant.phone}`
        : `@${participant.digits}`,
    }));
});

const adjustScroll = () => {
  nextTick(() => {
    const selectedElement = listRef.value?.querySelector(
      `#group-mention-item-${selectedIndex.value}`
    );
    selectedElement?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
  });
};

const onSelect = () => {
  const item = items.value[selectedIndex.value];
  if (item) emit('selectParticipant', item);
};

useKeyboardNavigableList({
  items,
  onSelect,
  adjustScroll,
  selectedIndex,
});

watch(items, newItems => {
  if (newItems.length < selectedIndex.value + 1) {
    selectedIndex.value = 0;
  }
});

const onHover = index => {
  selectedIndex.value = index;
};

const onClickSelect = index => {
  selectedIndex.value = index;
  onSelect();
};
</script>

<template>
  <div>
    <ul
      v-if="items.length"
      ref="listRef"
      class="vertical dropdown menu mention--box bg-n-solid-1 p-1 rounded-xl text-sm overflow-auto absolute w-full z-20 shadow-md left-0 leading-[1.2] bottom-full max-h-[12.5rem] border border-solid border-n-strong"
      role="listbox"
    >
      <li
        v-for="(item, index) in items"
        :id="`group-mention-item-${index}`"
        :key="item.id"
      >
        <div
          :class="{ 'bg-n-alpha-black2': index === selectedIndex }"
          class="flex items-center px-2 py-1 rounded-md cursor-pointer"
          role="option"
          @click="onClickSelect(index)"
          @mouseover="onHover(index)"
        >
          <div class="ltr:mr-2 rtl:ml-2">
            <Avatar :src="item.avatar" :name="item.name" rounded-full />
          </div>
          <div
            class="overflow-hidden flex-1 max-w-full whitespace-nowrap text-ellipsis"
          >
            <h5
              class="overflow-hidden mb-0 text-sm whitespace-nowrap text-n-slate-11 text-ellipsis"
              :class="{ 'text-n-slate-12': index === selectedIndex }"
            >
              {{ item.name }}
            </h5>
            <div
              class="overflow-hidden text-xs whitespace-nowrap text-ellipsis text-n-slate-10"
              :class="{ 'text-n-slate-11': index === selectedIndex }"
            >
              {{ item.handle }}
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>
